import { compact, orderBy } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { SuggestionForSearchAndEvidence } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getSuggestionAsSearchQuery } from '@/modules/aiSuggestions/aiSuggestions.utils';
import { suggestionEvidenceAdapter } from '@/modules/aiSuggestions/evidence';
import { useMultiDocumentSearch } from '@/modules/documents/hooks/useMultiDocumentSearch';
import { EntityType } from '@/modules/entities/types/EntityType';
import { diagnostics } from '@/utils/diagnostics';

import { SelectedSuggestionEvidenceContext } from './SelectedSuggestionEvidence.context';

function useContextValue({
  entityType,
  initialSelectedSuggestion,
}: SelectedSuggestionEvidenceProviderProps): SelectedSuggestionEvidenceContext {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { doMultiDocSearch } = useMultiDocumentSearch();

  const [documentPanelProps, setDocumentPanelProps] = useState<
    SelectedSuggestionEvidenceContext['documentPanelProps']
  >({
    activeDocumentViewerTab: undefined,
    activeDocumentId: undefined,
    annotations: undefined,
    activeAnnotationIndex: undefined,
    searchQuery: '',
    searchLoading: false,
  });

  // Track the last located suggestion so repeated clicks cycle through annotations
  // instead of resetting to the first one, mirroring the CitationPopper pattern.
  const lastLocatedSuggestionIdRef = useRef<string | null>(null);

  // Use a ref to read current documentPanelProps inside the callback without
  // adding it as a dependency (which would cause the effect for
  // initialSelectedSuggestion to re-trigger and inadvertently cycle).
  const documentPanelPropsRef = useRef(documentPanelProps);
  documentPanelPropsRef.current = documentPanelProps;

  const locateSuggestion = useCallback(
    async (suggestion: SuggestionForSearchAndEvidence) => {
      const isSameSuggestion =
        suggestion.id === lastLocatedSuggestionIdRef.current;

      // If the user clicks the same suggestion again and annotations are already
      // loaded, cycle to the next annotation instead of refetching.
      const currentProps = documentPanelPropsRef.current;
      if (isSameSuggestion) {
        // Ignore repeated clicks while we're still searching for the same
        // suggestion so we don't dispatch duplicate searches.
        if (currentProps.searchLoading) {
          return;
        }

        if (currentProps.annotations?.length) {
          setDocumentPanelProps((prev) => {
            const annotations = prev.annotations;
            if (!annotations?.length) return prev;

            const currentIndex = prev.activeAnnotationIndex ?? 0;
            const nextIndex = (currentIndex + 1) % annotations.length;
            const nextDocumentId = annotations[nextIndex]?.documentID;

            return {
              ...prev,
              activeAnnotationIndex: nextIndex,
              // Update the active document if the next annotation is on a different one
              activeDocumentId: nextDocumentId ?? prev.activeDocumentId,
            };
          });
          return;
        }
      }

      lastLocatedSuggestionIdRef.current = suggestion.id;

      // Every suggestion shape stores its source paragraphs on evidence rows.
      const annotations = orderBy(
        suggestionEvidenceAdapter(suggestion.evidence),
        ['documentID']
      );
      if (
        !suggestion.file?.document?.id &&
        !suggestion.kgNode &&
        annotations.length === 0
      ) {
        diagnostics.warn(
          `Trying to get evidence for a suggestion without a source document, KGNode, or file paragraphs. suggestionID=${suggestion.id}`
        );
        return;
      }

      // File paragraphs identify the source document and regions, so show them
      // without running an ad hoc document search.
      if (annotations.length > 0) {
        setDocumentPanelProps({
          activeDocumentViewerTab: 'document',
          activeDocumentId: annotations[0]?.documentID,
          annotations,
          activeAnnotationIndex: 0,
          searchQuery: getSuggestionAsSearchQuery(suggestion, entityType),
          searchLoading: false,
        });
        return;
      }

      // Suggestions without a matched paragraph can still open their source
      // document, but they have no precomputed highlight.
      if (suggestion.file?.document?.id) {
        const suggestionDocumentId = suggestion.file.document.id;
        setDocumentPanelProps({
          activeDocumentViewerTab: 'document',
          activeDocumentId: suggestionDocumentId,
          annotations: undefined,
          activeAnnotationIndex: undefined,
          searchQuery: getSuggestionAsSearchQuery(suggestion, entityType),
          searchLoading: false,
        });
        return;
      }

      // OTHERWISE... we need to do an adhoc search for the evidence...

      const documentIds = compact(
        suggestion.kgNode?.mergedFrom?.map((n) => n?.file?.document?.id)
      );

      setDocumentPanelProps({
        activeDocumentViewerTab: 'document',
        // Don't change the active document in the viewer until the search results come back
        activeDocumentId: undefined,
        searchQuery: getSuggestionAsSearchQuery(suggestion, entityType),
        annotations: undefined,
        activeAnnotationIndex: undefined,
        searchLoading: true,
      });

      // Use the node name to search for the suggestion's evidence in the documents
      const searchQuery = getSuggestionAsSearchQuery(suggestion, entityType);

      try {
        const boundingRegions = await doMultiDocSearch(
          searchQuery,
          documentIds
        );

        setDocumentPanelProps((prev) => ({
          ...prev,
          activeDocumentId: boundingRegions[0]?.documentID,
          annotations: boundingRegions,
          activeAnnotationIndex: 0,
          searchLoading: false,
        }));
      } catch (e) {
        showFeedback(
          'Unable to locate evidence for this suggestion. Please try again later.'
        );
        reportError('error in multi-doc evidence search', e as Error);
      } finally {
        setDocumentPanelProps((prev) => ({
          ...prev,
          searchLoading: false,
        }));
      }
    },
    [doMultiDocSearch, entityType, reportError, showFeedback]
  );

  // When initialSelectedSuggestion is provided, locate it immediately
  useEffect(() => {
    if (initialSelectedSuggestion) {
      void locateSuggestion(initialSelectedSuggestion);
    }
  }, [initialSelectedSuggestion, locateSuggestion]);

  return {
    locateSuggestion,
    documentPanelProps,
    setDocumentPanelProps,
  };
}

interface SelectedSuggestionEvidenceProviderProps {
  entityType?: EntityType | null | undefined;
  initialSelectedSuggestion?: SuggestionForSearchAndEvidence;
}

export function SelectedSuggestionEvidenceProvider({
  children,
  entityType,
  initialSelectedSuggestion,
}: PropsWithChildren<SelectedSuggestionEvidenceProviderProps>) {
  const value = useContextValue({ entityType, initialSelectedSuggestion });

  return (
    <SelectedSuggestionEvidenceContext.Provider value={value}>
      {children}
    </SelectedSuggestionEvidenceContext.Provider>
  );
}
